/*
 * HeicSafeImage Component
 *
 * A drop-in replacement for <img> that automatically detects and converts
 * HEIC images (including those with wrong extensions like .jpg) to JPEG.
 *
 * Features:
 * - Magic byte detection for HEIC content
 * - Client-side conversion using libheif-js (heic-decode) - supports MiPr profile
 * - IndexedDB caching to avoid re-conversion
 * - Fallback to regular image display for non-HEIC
 * - Maintains all standard img attributes
 *
 * Author: System
 * Date: 2025-09-30
 * Updated: 2025-10-01 - Migrated from heic2any to heic-decode for MiPr support
 */

import React, { useState, useEffect, useRef, ImgHTMLAttributes } from 'react'
import decode from 'heic-decode'

interface HeicSafeImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string
  alt: string
  onHeicDetected?: (wasHeic: boolean) => void
}

// IndexedDB cache management
class ImageCache {
  private dbName = 'heic-image-cache'
  private storeName = 'converted-images'
  private db: IDBDatabase | null = null

  async init(): Promise<void> {
    if (this.db) return

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'url' })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
    })
  }

  async get(url: string): Promise<string | null> {
    try {
      await this.init()

      if (!this.db) return null

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([this.storeName], 'readonly')
        const store = transaction.objectStore(this.storeName)
        const request = store.get(url)

        request.onsuccess = () => {
          const result = request.result

          if (!result) {
            resolve(null)

            return
          }

          // Check if cache is older than 30 days
          const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000

          if (Date.now() - result.timestamp > thirtyDaysMs) {
            this.delete(url) // Clean up old cache
            resolve(null)

            return
          }

          resolve(result.dataUrl)
        }

        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('Cache read error:', error)

      return null
    }
  }

  async set(url: string, dataUrl: string): Promise<void> {
    try {
      await this.init()

      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([this.storeName], 'readwrite')
        const store = transaction.objectStore(this.storeName)
        const request = store.put({
          url,
          dataUrl,
          timestamp: Date.now(),
        })

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('Cache write error:', error)
    }
  }

  async delete(url: string): Promise<void> {
    try {
      await this.init()

      if (!this.db) return

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([this.storeName], 'readwrite')
        const store = transaction.objectStore(this.storeName)
        const request = store.delete(url)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    } catch (error) {
      console.warn('Cache delete error:', error)
    }
  }
}

const imageCache = new ImageCache()

/**
 * Checks if blob content has HEIC magic bytes
 */
async function hasHeicMagicBytes(blob: Blob): Promise<boolean> {
  try {
    const arrayBuffer = await blob.slice(0, 12).arrayBuffer()
    const bytes = new Uint8Array(arrayBuffer)

    if (bytes.length < 12) return false

    // Check for "ftyp" signature at bytes 4-7
    const ftypMatch =
      bytes[4] === 0x66 && // 'f'
      bytes[5] === 0x74 && // 't'
      bytes[6] === 0x79 && // 'y'
      bytes[7] === 0x70 // 'p'

    if (!ftypMatch) return false

    // Convert bytes 8-11 to string for brand check
    const brandString = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11])

    // Check for HEIC brand identifiers
    const heicBrands = ['heic', 'heix', 'hevc', 'hevx', 'mif1', 'msf1']

    return heicBrands.some((brand) => brandString.startsWith(brand))
  } catch (error) {
    console.warn('Error checking magic bytes:', error)

    return false
  }
}

/**
 * Fetches and converts HEIC image if needed
 */
async function fetchAndConvertIfNeeded(url: string): Promise<{ dataUrl: string; wasHeic: boolean }> {
  // Check cache first
  const cached = await imageCache.get(url)

  if (cached) {
    console.log('📦 Using cached converted image:', url)

    return { dataUrl: cached, wasHeic: true }
  }

  // Fetch the image as blob
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`)
  }

  const blob = await response.blob()

  // Check if it's HEIC
  const isHeic = await hasHeicMagicBytes(blob)

  if (!isHeic) {
    // Not HEIC, return original URL
    return { dataUrl: url, wasHeic: false }
  }

  console.log('🔍 Detected HEIC content in displayed image:', url)

  // Convert HEIC to JPEG using libheif-js (supports MiPr profile)
  try {
    // Convert blob to ArrayBuffer then to Uint8Array
    const arrayBuffer = await blob.arrayBuffer()
    const buffer = new Uint8Array(arrayBuffer)

    // Decode HEIC to raw image data using libheif-js
    const { width, height, data } = await decode({ buffer })

    // Create canvas and draw decoded image data
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Failed to get canvas 2D context')
    }

    // Create ImageData from decoded RGBA buffer (data is already Uint8ClampedArray)
    const imageData = new ImageData(new Uint8ClampedArray(data), width, height)
    ctx.putImageData(imageData, 0, 0)

    // Convert canvas to data URL
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9)

    // Cache the converted image
    await imageCache.set(url, dataUrl)

    console.log('✅ Converted and cached HEIC image (libheif-js with MiPr support):', url)

    return { dataUrl, wasHeic: true }
  } catch (error) {
    console.error('❌ Failed to convert HEIC image:', url, error)

    // Fallback to original URL
    return { dataUrl: url, wasHeic: false }
  }
}

/**
 * HeicSafeImage Component
 *
 * Drop-in replacement for <img> that handles HEIC images transparently
 */
const HeicSafeImage: React.FC<HeicSafeImageProps> = ({ src, alt, onHeicDetected, ...imgProps }) => {
  const [displaySrc, setDisplaySrc] = useState<string>(src)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const mountedRef = useRef<boolean>(true)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadImage = async () => {
      if (!src) {
        setIsLoading(false)

        return
      }

      setIsLoading(true)

      try {
        const { dataUrl, wasHeic } = await fetchAndConvertIfNeeded(src)

        if (cancelled || !mountedRef.current) return

        setDisplaySrc(dataUrl)

        if (onHeicDetected) {
          onHeicDetected(wasHeic)
        }
      } catch (error) {
        console.error('Error loading image:', src, error)

        if (cancelled || !mountedRef.current) return
        // Fallback to original src
        setDisplaySrc(src)
      } finally {
        if (!cancelled && mountedRef.current) {
          setIsLoading(false)
        }
      }
    }

    loadImage()

    return () => {
      cancelled = true
    }
  }, [src, onHeicDetected])

  const handleLoad = () => {
    setIsLoading(false)
  }

  const handleError = () => {
    setIsLoading(false)
  }

  return (
    <img
      {...imgProps}
      src={displaySrc}
      alt={alt}
      onLoad={handleLoad}
      onError={handleError}
      style={{
        ...imgProps.style,
        opacity: isLoading ? 0.5 : 1,
        transition: 'opacity 0.2s ease-in-out',
      }}
    />
  )
}

export default HeicSafeImage
